import type { Bbox2 } from "../geo/bbox";
import { t } from "../i18n";
import type { Annotation } from "./Annotation";
import { drawAnnotations } from "./Annotation";
import type { OrthoStack } from "./OrthoStack";
import { compositeStack } from "./PlanRenderer";

export type PlanTool = "pan" | "pen" | "text";

/** How long to wait after the last pan/zoom event before actually re-reading raster data —
 *  see `scheduleComposite`. Short enough to feel responsive, long enough that a fast drag or
 *  scroll doesn't spend it re-fetching tiles for a view it's already left behind. */
const COMPOSITE_DEBOUNCE_MS = 150;

/**
 * Interactive 2D canvas for the orthophoto plan mode: pan/zoom over the loaded raster stack,
 * plus freehand/text annotation. Kept entirely separate from the three.js viewer — this is a
 * flat, top-down composite, not a draped 3D surface (see `PlanRenderer`).
 *
 * View state is a centre + world-units-per-device-pixel scale rather than a stored extent, so
 * pan/zoom/resize all fall out of the same two numbers instead of needing to be kept in sync
 * with each other.
 */
export class PlanView {
  tool: PlanTool = "pan";
  penColor = "#e63946";
  /** Pen thickness in *screen* pixels at the moment a stroke is drawn — see `Annotation.ts`. */
  penWidthPx = 6;
  annotations: Annotation[] = [];

  private centerX = 0;
  private centerY = 0;
  private metresPerPixel = 1;
  private ready = false;
  /** Once the user has panned or zoomed, a newly added layer no longer yanks the view back
   *  to fit everything loaded so far — same reasoning a map viewer's "auto zoom to layers"
   *  usually stops once you've taken the view somewhere yourself. */
  private userAdjustedView = false;

  private lastComposite: ImageBitmap | null = null;
  private compositeExtent: Bbox2 | null = null;
  private generation = 0;
  private controller: AbortController | null = null;
  private composeTimer = 0;

  private dragging = false;
  private lastPointer: { x: number; y: number } | null = null;
  private activeStroke: { points: Array<{ x: number; y: number }>; color: string; widthWorld: number } | null = null;

  private readonly ctx: CanvasRenderingContext2D;
  private readonly resizeObserver: ResizeObserver;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly stack: OrthoStack,
    private readonly onChange?: () => void,
  ) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.ctx = ctx;
    canvas.dataset.tool = this.tool;

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(canvas);
    this.handleResize();

    canvas.addEventListener("wheel", (e) => this.onWheel(e), { passive: false });
    canvas.addEventListener("pointerdown", (e) => this.onPointerDown(e));
    canvas.addEventListener("pointermove", (e) => this.onPointerMove(e));
    canvas.addEventListener("pointerup", (e) => this.onPointerUp(e));
    canvas.addEventListener("pointerleave", (e) => this.onPointerUp(e));
  }

  /** Current world extent covering the whole canvas — used as the export centre. */
  get extent(): Bbox2 {
    const w = (this.canvas.width / 2) * this.metresPerPixel;
    const h = (this.canvas.height / 2) * this.metresPerPixel;
    return { minX: this.centerX - w, maxX: this.centerX + w, minY: this.centerY - h, maxY: this.centerY + h };
  }

  get center(): { x: number; y: number } {
    return { x: this.centerX, y: this.centerY };
  }

  fitToExtent(bbox: Bbox2): void {
    const width = Math.max(1e-6, bbox.maxX - bbox.minX);
    const height = Math.max(1e-6, bbox.maxY - bbox.minY);
    this.centerX = (bbox.minX + bbox.maxX) / 2;
    this.centerY = (bbox.minY + bbox.maxY) / 2;
    const padding = 1.08;
    this.metresPerPixel = Math.max(width / this.canvas.width, height / this.canvas.height) * padding;
    this.ready = true;
    this.requestComposite();
  }

  /**
   * Call whenever the stack's contents or visibility/order change. Keeps auto-fitting to the
   * combined extent of everything loaded so far — including a layer added after the first,
   * which may well lie outside whatever's currently in view — until the user takes the view
   * somewhere themselves (see `userAdjustedView`), at which point a new layer is composited
   * into the existing view rather than moving it.
   */
  refresh(): void {
    const extent = this.stack.extent();
    if (!extent) return;
    if (!this.userAdjustedView) this.fitToExtent(extent);
    else this.requestComposite();
  }

  setTool(tool: PlanTool): void {
    this.tool = tool;
    this.canvas.dataset.tool = tool;
  }

  /** Re-measures the canvas against its current on-screen size — call after unhiding it, in
   *  case its `ResizeObserver` callback hasn't run yet (see `App.setMode`). */
  resize(): void {
    this.handleResize();
  }

  undo(): void {
    this.annotations.pop();
    this.redrawOverlay();
    this.onChange?.();
  }

  clearAnnotations(): void {
    this.annotations = [];
    this.redrawOverlay();
    this.onChange?.();
  }

  dispose(): void {
    this.resizeObserver.disconnect();
    window.clearTimeout(this.composeTimer);
    this.controller?.abort();
    this.lastComposite?.close();
  }

  // ---------------------------------------------------------------------- rendering

  private handleResize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(rect.width * dpr));
    const height = Math.max(1, Math.round(rect.height * dpr));
    if (this.canvas.width === width && this.canvas.height === height) return;
    this.canvas.width = width;
    this.canvas.height = height;
    if (this.ready) this.requestComposite();
  }

  /**
   * Debounced entry point for pan/zoom: draws the last real composite stretched/translated to
   * match the view *immediately* (see `redrawOverlay`/`drawCachedComposite`), then waits for
   * movement to settle before actually re-reading raster data. Re-fetching on every single
   * `pointermove` — several per frame during a fast drag — is what made panning/zooming feel
   * stuck: nothing moved on screen until each fetch resolved. Deliberate one-off changes
   * (`fitToExtent`, adding/removing a layer) call `requestComposite` directly instead — see
   * `refresh`.
   */
  private scheduleComposite(delayMs = COMPOSITE_DEBOUNCE_MS): void {
    this.redrawOverlay();
    window.clearTimeout(this.composeTimer);
    this.composeTimer = window.setTimeout(() => this.requestComposite(), delayMs);
  }

  private requestComposite(): void {
    if (!this.ready || this.canvas.width === 0) return;
    const generation = ++this.generation;
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const extent = this.extent;
    const target = { ctx: offscreenCtx(this.canvas.width, this.canvas.height), pixelWidth: this.canvas.width, pixelHeight: this.canvas.height, extent };

    void compositeStack(this.stack.paintOrder(), target, controller.signal).then(async () => {
      if (generation !== this.generation || controller.signal.aborted) return;
      const bitmap = await createImageBitmap(target.ctx.canvas);
      if (generation !== this.generation) {
        bitmap.close();
        return;
      }
      this.lastComposite?.close();
      this.lastComposite = bitmap;
      this.compositeExtent = extent;
      this.redrawOverlay();
      this.onChange?.();
    });
  }

  /** Redraws the cached composite (stretched/translated to match the *current* view — see
   *  `drawCachedComposite`) plus annotations and any in-progress stroke. Cheap enough to call
   *  on every pointer-move, unlike a full re-composite: no raster data is read here. */
  private redrawOverlay(): void {
    const { ctx, canvas } = this;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (this.lastComposite && this.compositeExtent) {
      this.drawCachedComposite();
    } else {
      ctx.fillStyle = "#1b1f24";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    const extent = this.extent;
    const all = this.activeStroke ? [...this.annotations, { type: "stroke" as const, ...this.activeStroke }] : this.annotations;
    drawAnnotations(ctx, all, extent, canvas.width, canvas.height);
  }

  /**
   * Places the last real composite at wherever its own extent now falls inside the *current*
   * view, instead of assuming they still match — exactly like a map placeholder tile scaling
   * while a sharper one loads. Panning/zooming therefore looks continuous even though the
   * underlying image is stale until `requestComposite` finishes.
   */
  private drawCachedComposite(): void {
    const src = this.compositeExtent!;
    const extent = this.extent;
    const toPx = (x: number, y: number) => ({
      x: ((x - extent.minX) / (extent.maxX - extent.minX)) * this.canvas.width,
      y: ((extent.maxY - y) / (extent.maxY - extent.minY)) * this.canvas.height,
    });
    const topLeft = toPx(src.minX, src.maxY);
    const bottomRight = toPx(src.maxX, src.minY);
    const width = bottomRight.x - topLeft.x;
    const height = bottomRight.y - topLeft.y;
    if (width <= 0 || height <= 0) return;
    this.ctx.drawImage(this.lastComposite!, topLeft.x, topLeft.y, width, height);
  }

  // ---------------------------------------------------------------------- pointer / wheel

  private toCanvasPx(e: PointerEvent | WheelEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    return { x: (e.clientX - rect.left) * dpr, y: (e.clientY - rect.top) * dpr };
  }

  private toWorld(px: { x: number; y: number }): { x: number; y: number } {
    return {
      x: this.centerX + (px.x - this.canvas.width / 2) * this.metresPerPixel,
      y: this.centerY - (px.y - this.canvas.height / 2) * this.metresPerPixel,
    };
  }

  private onWheel(e: WheelEvent): void {
    if (!this.ready) return;
    e.preventDefault();
    this.userAdjustedView = true;
    const px = this.toCanvasPx(e);
    const before = this.toWorld(px);
    const factor = Math.pow(1.1, -e.deltaY / 100);
    this.metresPerPixel = clamp(this.metresPerPixel * factor, 1e-5, 1e6);
    const after = this.toWorld(px);
    this.centerX += before.x - after.x;
    this.centerY += before.y - after.y;
    this.scheduleComposite();
  }

  private onPointerDown(e: PointerEvent): void {
    if (!this.ready) return;
    this.canvas.setPointerCapture(e.pointerId);
    const px = this.toCanvasPx(e);
    if (this.tool === "pan") {
      this.dragging = true;
      this.lastPointer = px;
    } else if (this.tool === "pen") {
      const world = this.toWorld(px);
      this.activeStroke = { points: [world], color: this.penColor, widthWorld: this.penWidthPx * this.metresPerPixel };
    } else if (this.tool === "text") {
      const world = this.toWorld(px);
      const text = window.prompt(t().textPrompt);
      if (text && text.trim()) {
        this.annotations.push({ type: "text", x: world.x, y: world.y, text: text.trim(), color: this.penColor, sizeWorld: 14 * this.metresPerPixel });
        this.redrawOverlay();
        this.onChange?.();
      }
    }
  }

  private onPointerMove(e: PointerEvent): void {
    if (!this.ready) return;
    const px = this.toCanvasPx(e);
    if (this.dragging && this.lastPointer) {
      const dx = px.x - this.lastPointer.x;
      const dy = px.y - this.lastPointer.y;
      this.userAdjustedView = true;
      this.centerX -= dx * this.metresPerPixel;
      this.centerY += dy * this.metresPerPixel;
      this.lastPointer = px;
      this.scheduleComposite();
    } else if (this.activeStroke) {
      this.activeStroke.points.push(this.toWorld(px));
      this.redrawOverlay();
    }
  }

  private onPointerUp(e: PointerEvent): void {
    if (this.canvas.hasPointerCapture(e.pointerId)) this.canvas.releasePointerCapture(e.pointerId);
    const wasPanning = this.dragging;
    this.dragging = false;
    this.lastPointer = null;
    // Releasing the drag is as good a signal as any that the gesture is over — no need to
    // wait out the full debounce once the pointer has actually lifted.
    if (wasPanning) this.scheduleComposite(50);
    if (this.activeStroke) {
      if (this.activeStroke.points.length > 1) {
        this.annotations.push({ type: "stroke", ...this.activeStroke });
        this.onChange?.();
      }
      this.activeStroke = null;
      this.redrawOverlay();
    }
  }
}

function offscreenCtx(width: number, height: number): CanvasRenderingContext2D {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas context unavailable");
  return ctx;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
